import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RxCross2 } from "react-icons/rx";
import { postData, API_ENABLED, getData } from "../lib/api";

/**
 * The "Get A Quote" form in the site header. Submissions land in the admin
 * panel under Quote requests.
 */
export default function QuoteModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState([]);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service_id: "",
    budget_range: "",
    details: "",
    website: "", // honeypot, must stay empty
  });

  useEffect(() => {
    if (!open) return;

    getData("/services", { params: { lang: i18n.language } }).then((data) => {
      if (Array.isArray(data)) setServices(data);
    });
  }, [open, i18n.language]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setErrors({});

    if (!API_ENABLED) {
      toast.error(t("quote.offline", "Online requests are not available right now. Please call or email us."));
      return;
    }

    setSending(true);

    try {
      const response = await postData("/quote-requests", form);
      toast.success(response.message || t("quote.success", "Thank you. We will contact you shortly."));
      setForm({
        name: "", company: "", email: "", phone: "",
        service_id: "", budget_range: "", details: "", website: "",
      });
      setTimeout(() => onClose?.(), 1200);
    } catch (error) {
      const fieldErrors = error.payload?.errors || {};
      setErrors(fieldErrors);
      toast.error(error.payload?.message || t("quote.error", "Your request could not be sent. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const BUDGETS = ["< 50,000 USD", "50,000 – 250,000 USD", "250,000 – 1M USD", "> 1M USD"];

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-mstheme">
              {t("quote.title", "Request a Quotation")}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {t("quote.subtitle", "Tell us about your project and our team will respond with a quotation.")}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-gray-400 hover:text-gray-700">
            <RxCross2 size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 px-6 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("quote.name", "Full name")} required error={errors.name}>
              <input className={inputClass} value={form.name} onChange={update("name")} required />
            </Field>
            <Field label={t("quote.company", "Company")} error={errors.company}>
              <input className={inputClass} value={form.company} onChange={update("company")} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("quote.email", "Email address")} required error={errors.email}>
              <input type="email" className={inputClass} value={form.email} onChange={update("email")} required />
            </Field>
            <Field label={t("quote.phone", "Phone number")} error={errors.phone}>
              <input className={inputClass} value={form.phone} onChange={update("phone")} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("quote.service", "Service required")} error={errors.service_id}>
              <select className={inputClass} value={form.service_id} onChange={update("service_id")}>
                <option value="">{t("quote.choose", "— Please choose —")}</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("quote.budget", "Estimated budget")} error={errors.budget_range}>
              <select className={inputClass} value={form.budget_range} onChange={update("budget_range")}>
                <option value="">{t("quote.choose", "— Please choose —")}</option>
                {BUDGETS.map((budget) => (
                  <option key={budget} value={budget}>
                    {budget}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t("quote.details", "Project details")} error={errors.details}>
            <textarea rows={4} className={inputClass} value={form.details} onChange={update("details")} />
          </Field>

          {/* Honeypot: hidden from people, filled by bots */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={update("website")}
            className="hidden"
          />

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            <button type="button" onClick={onClose} className="rounded px-4 py-2 text-gray-600 hover:bg-gray-100">
              {t("quote.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              disabled={sending}
              className="rounded bg-mstheme px-5 py-2 font-medium text-white hover:opacity-90 disabled:opacity-60"
            >
              {sending ? t("quote.sending", "Sending…") : t("quote.submit", "Send request")}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer position="top-center" autoClose={4000} />
    </div>
  );
}

const inputClass =
  "w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-mstheme";

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-mstheme"> *</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{Array.isArray(error) ? error[0] : error}</p>}
    </div>
  );
}
